/**
 * Canvas 2D Overlay Renderer
 *
 * Renders the tracing image on a 2D canvas overlaid on the camera feed.
 * Handles positioning, scaling, rotation, and opacity.
 */

export interface ImageTransform {
  x: number; // Center X position in canvas coordinates
  y: number; // Center Y position in canvas coordinates
  scale: number; // Scale factor (1 = original size relative to canvas)
  rotation: number; // Rotation in radians
  flipH: boolean; // Horizontal flip
  flipV: boolean; // Vertical flip
}

export interface CanvasOverlayConfig {
  canvas: HTMLCanvasElement;
  width?: number;
  height?: number;
}

export class CanvasOverlay {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private image: HTMLImageElement | HTMLCanvasElement | null = null;
  private imageAspect: number = 1;
  private transform: ImageTransform = {
    x: 0,
    y: 0,
    scale: 1,
    rotation: 0,
    flipH: false,
    flipV: false,
  };
  private opacity: number = 0.5;
  private visible: boolean = false;
  private animationFrameId: number | null = null;

  constructor(config: CanvasOverlayConfig) {
    this.canvas = config.canvas;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context from canvas');
    }
    this.ctx = ctx;

    // Set canvas size
    this.resize(config.width || 800, config.height || 800);
  }

  /**
   * Resize the canvas
   */
  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
    // Center transform on resize
    if (this.transform.x === 0 && this.transform.y === 0) {
      this.transform.x = width / 2;
      this.transform.y = height / 2;
    }
    this.render();
  }

  /**
   * Set the image to display
   */
  setImage(image: HTMLImageElement | HTMLCanvasElement | null): void {
    this.image = image;
    if (image) {
      this.imageAspect = image.width / image.height;
      this.visible = true;
    } else {
      this.visible = false;
    }
    this.render();
  }

  /**
   * Load image from blob
   */
  async loadImageFromBlob(blob: Blob): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.setImage(img);
        URL.revokeObjectURL(img.src);
        resolve();
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image'));
      };
      img.src = URL.createObjectURL(blob);
    });
  }

  /**
   * Set the transform
   */
  setTransform(transform: Partial<ImageTransform>): void {
    this.transform = { ...this.transform, ...transform };
    this.render();
  }

  /**
   * Get the current transform
   */
  getTransform(): ImageTransform {
    return { ...this.transform };
  }

  /**
   * Set opacity (0-1)
   */
  setOpacity(opacity: number): void {
    this.opacity = Math.max(0, Math.min(1, opacity));
    this.render();
  }

  /**
   * Get current opacity
   */
  getOpacity(): number {
    return this.opacity;
  }

  /**
   * Set visibility
   */
  setVisible(visible: boolean): void {
    this.visible = visible;
    this.render();
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Apply position offset (in canvas coordinates)
   */
  applyOffset(dx: number, dy: number): void {
    this.transform.x += dx;
    this.transform.y += dy;
    this.render();
  }

  /**
   * Apply scale change
   */
  applyScale(factor: number): void {
    this.transform.scale = Math.max(0.1, Math.min(10, this.transform.scale * factor));
    this.render();
  }

  /**
   * Apply rotation change (in radians)
   */
  applyRotation(delta: number): void {
    this.transform.rotation += delta;
    this.render();
  }

  /**
   * Toggle horizontal flip
   */
  toggleFlipH(): void {
    this.transform.flipH = !this.transform.flipH;
    this.render();
  }

  /**
   * Toggle vertical flip
   */
  toggleFlipV(): void {
    this.transform.flipV = !this.transform.flipV;
    this.render();
  }

  /**
   * Reset transform to defaults (centered)
   */
  resetTransform(): void {
    this.transform = {
      x: this.canvas.width / 2,
      y: this.canvas.height / 2,
      scale: 1,
      rotation: 0,
      flipH: false,
      flipV: false,
    };
    this.render();
  }

  /**
   * Set position from marker pose
   * Converts marker world coordinates to canvas coordinates
   */
  setPositionFromMarker(
    markerX: number,
    markerY: number,
    markerScale: number,
    markerRotation: number
  ): void {
    // Convert marker coordinates to canvas coordinates
    // Marker coordinates are typically in a different coordinate system
    // This is a simple linear mapping - adjust based on your calibration
    const canvasScale = Math.min(this.canvas.width, this.canvas.height);

    this.transform.x = this.canvas.width / 2 + markerX * canvasScale;
    this.transform.y = this.canvas.height / 2 + markerY * canvasScale;
    this.transform.scale = markerScale;
    this.transform.rotation = markerRotation;

    this.render();
  }

  /**
   * Render the overlay
   */
  render(): void {
    const { ctx, canvas, image } = this;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Don't render if no image or not visible
    if (!image || !this.visible) {
      return;
    }

    const { x, y, scale, rotation, flipH, flipV } = this.transform;

    // Calculate image dimensions
    // Base size: fit image within canvas while maintaining aspect ratio
    const baseSize = Math.min(canvas.width, canvas.height) * 0.8;
    let drawWidth: number;
    let drawHeight: number;

    if (this.imageAspect > 1) {
      // Landscape
      drawWidth = baseSize * scale;
      drawHeight = (baseSize / this.imageAspect) * scale;
    } else {
      // Portrait or square
      drawWidth = (baseSize * this.imageAspect) * scale;
      drawHeight = baseSize * scale;
    }

    // Save context state
    ctx.save();

    // Set opacity
    ctx.globalAlpha = this.opacity;

    // Move to center position
    ctx.translate(x, y);

    // Apply rotation
    ctx.rotate(rotation);

    // Apply flips
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

    // Draw image centered
    ctx.drawImage(
      image,
      -drawWidth / 2,
      -drawHeight / 2,
      drawWidth,
      drawHeight
    );

    // Restore context state
    ctx.restore();
  }

  /**
   * Start animation loop (for continuous rendering if needed)
   */
  startAnimationLoop(): void {
    if (this.animationFrameId !== null) return;

    const animate = () => {
      this.render();
      this.animationFrameId = requestAnimationFrame(animate);
    };
    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Stop animation loop
   */
  stopAnimationLoop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Get canvas dimensions
   */
  getDimensions(): { width: number; height: number } {
    return {
      width: this.canvas.width,
      height: this.canvas.height,
    };
  }

  /**
   * Destroy the overlay (cleanup)
   */
  destroy(): void {
    this.stopAnimationLoop();
    this.image = null;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}

/**
 * Create and initialize a canvas overlay
 */
export function createCanvasOverlay(canvasId: string = 'overlay-canvas'): CanvasOverlay {
  const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
  if (!canvas) {
    throw new Error(`Canvas element #${canvasId} not found`);
  }

  // Size to viewport
  const size = Math.min(window.innerWidth, window.innerHeight);

  return new CanvasOverlay({
    canvas,
    width: size,
    height: size,
  });
}
